/**
 * StopWateringButton — shut a site down now.
 *
 * One press stops everything at one controller: the pumps first, confirmed,
 * then every valve. The ordering happens server-side (or on the site's own
 * bridge); this is the control and the confirmation for it.
 *
 * It asks before it acts. That is not the usual reflex for a safety control —
 * an emergency stop with a dialog in front of it is a worse emergency stop —
 * but this one is a button on a page anybody may be scrolling, it stops an
 * entire farm, and the site stays HELD afterwards until somebody with an admin
 * seat resumes it. A mis-tap is not undone by tapping again. The dialog names
 * what is about to stop and takes an optional note, and nothing else stands in
 * the way: no typing the site's name, no second confirmation.
 */

import { useState } from 'react';
import { OctagonX } from 'lucide-react';
import Button from '../ui/Button';

interface StopWateringButtonProps {
  controllerName: string;
  /** How many valves are reported open right now, if the caller knows. */
  openValves?: number;
  busy?: boolean;
  disabled?: boolean;
  /** Compact form for a card; the full form is for a page header. */
  size?: 'sm' | 'md';
  className?: string;
  onStop: (reason?: string) => Promise<void> | void;
}

export default function StopWateringButton({
  controllerName,
  openValves,
  busy = false,
  disabled = false,
  size = 'sm',
  className,
  onStop,
}: StopWateringButtonProps) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  const confirm = async () => {
    setAsking(false);
    const note = reason.trim();
    setReason('');
    await onStop(note || undefined);
  };

  return (
    <>
      <Button
        variant="danger"
        size={size}
        className={className}
        disabled={disabled || busy}
        isLoading={busy}
        onClick={() => setAsking(true)}
        aria-label={`Stop watering at ${controllerName}`}
      >
        <OctagonX className="h-4 w-4 sm:mr-1.5" />
        <span className="hidden sm:inline">Stop watering</span>
      </Button>

      {asking && (
        <div
          className="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stop-watering-title"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
                <OctagonX className="h-5 w-5 text-red-600" />
              </span>
              <div className="min-w-0">
                <h3 id="stop-watering-title" className="text-base font-semibold text-gray-900">
                  Stop watering at {controllerName}?
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  {typeof openValves === 'number' && openValves > 0
                    ? `${openValves} valve${openValves === 1 ? '' : 's'} will close. `
                    : ''}
                  The pumps are switched off first and have to confirm before any valve
                  closes.
                </p>
                {/* The half people forget, and the half that needs an admin to
                    undo. Saying it here is cheaper than explaining it later to
                    somebody wondering why the 4am program never ran. */}
                <p className="mt-2 text-sm font-medium text-gray-900">
                  The site then stays stopped — no program will run, including tonight's —
                  until someone resumes it.
                </p>
              </div>
            </div>

            <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="stop-reason">
              What is wrong? <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="stop-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Burst line at the main"
              maxLength={200}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown to whoever finds the site stopped.
            </p>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" size="sm" onClick={() => setAsking(false)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={() => void confirm()}>
                Stop watering now
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
